import React, { createRef, memo, useCallback, useEffect, useState } from 'react';

import RocketImage from 'assets/images/RocketImage';
import useDebounce from 'hooks/useDebounce';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { orderBumpService } from 'services';
import { PAGINATION_OPTION_PER_PAGE } from 'settings';
import { If, Divider } from 'shared';
import AlertDialog, { IAlertDialog } from 'shared/AlertDialog';
import { ButtonIcon, Column, Pagination, Row, Typography } from 'shared/Houston';
import { Spinner } from 'shared/Loader/styles';
import { selectMediaQuery, selectUser } from 'store/selectors';

import Cancel from '@eduzz/houston-icons/Cancel';
import SearchOutline from '@eduzz/houston-icons/SearchOutline';

import BumpItem from './BumpItem';
import Preview from './Preview';
import {
  Loading,
  Field,
  List,
  RowGrid,
  TypographyStart,
  TypographyEnd,
  TypographyCenter,
  ContainerPagination,
  EmptyContainer
} from './styles';

interface IProps {
  handleSetHasOrderBump: (value: boolean) => void;
}

const ListOrderBump: React.FC<IProps> = ({ handleSetHasOrderBump }) => {
  const mediaQuery = useSelector(selectMediaQuery);
  const { id } = useSelector(selectUser);
  const refAlertDialog = createRef<IAlertDialog>();

  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [firstLoading, setFirstLoading] = useState(true);
  const [hasOrderBump, setHasOrderBump] = useState<boolean | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [bumpId, setBumpId] = useState<number>();

  const handleChangeDebouncedSearch = useCallback(() => {
    if (search.length > 1) {
      return search;
    }

    return '';
  }, [search]);

  const debouncedSearch = useDebounce<string>(handleChangeDebouncedSearch());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['orderBumpList', page, perPage, debouncedSearch, id],
    refetchOnWindowFocus: true,
    queryFn({ signal }) {
      return orderBumpService.getOrderBumps(debouncedSearch, page, perPage, signal);
    }
  });

  const openModal = useCallback(
    (bumpIdParam: number) => {
      refetch();
      setOpen(true);
      setBumpId(bumpIdParam);
    },
    [refetch]
  );

  const closeDrawer = useCallback(() => {
    refetch();
    setOpen(false);
  }, [refetch]);

  useEffect(() => {
    if (data && data?.total == 0 && !search) {
      setHasOrderBump(false);
      handleSetHasOrderBump(false);
      return;
    }

    setHasOrderBump(true);
    handleSetHasOrderBump(true);
  }, [data, hasOrderBump, handleSetHasOrderBump, search]);

  useEffect(() => {
    if (!isLoading) {
      setFirstLoading(false);
    }
  }, [search, isLoading]);

  const handleCancelSearchBumps = useCallback(() => {
    setSearch('');
  }, []);

  return (
    <>
      <If condition={firstLoading}>
        <div style={{ height: 'calc(100vh - 123px)' }}>
          <Loading alignItems='center' justifyContent='center'>
            <Spinner width='72' height='72' />
          </Loading>
        </div>
      </If>
      <If condition={hasOrderBump === true}>
        <If condition={!mediaQuery.md}>
          <Divider margin='24px 0px 16px' />
        </If>
        <Row alignItems='center' justifyContent='space-between'>
          <Column style={{ width: 500 }}>
            <Field
              label='Buscar'
              value={search}
              maxLength={255}
              onChange={value => setSearch(value)}
              startAdornment={<SearchOutline />}
              placeholder='Filtrar Order Bump'
              endAdornment={
                <If condition={!!search}>
                  <ButtonIcon onClick={handleCancelSearchBumps}>
                    <Cancel />
                  </ButtonIcon>
                </If>
              }
            />
          </Column>
        </Row>
        <If condition={!mediaQuery.md}>
          <Divider margin='8px 0px 0px 0px' />
          <RowGrid>
            <TypographyStart weight='semibold'>Produto Principal</TypographyStart>
            <TypographyCenter weight='semibold'>Status</TypographyCenter>
            <TypographyEnd weight='semibold'>Ações</TypographyEnd>
          </RowGrid>
        </If>
        <AlertDialog ref={refAlertDialog} />
        <List>
          <If condition={isLoading}>
            <div style={{ height: 'calc(100vh - 352px)' }}>
              <Loading alignItems='center' justifyContent='center'>
                <Spinner width='72' height='72' />
              </Loading>
            </div>
          </If>
          <If condition={search && data?.bumps?.length === 0}>
            <EmptyContainer>
              <RocketImage />
              <Typography size='xxs' marginBottom>
                Nenhum resultado encontrado &#128532;
              </Typography>
              <Typography size='xs'>
                Tente ajustar o filtro de pesquisa para encontrar o Order Bump que está procurando
              </Typography>
            </EmptyContainer>
          </If>
          <If condition={!isLoading && (data?.total || 0) > 0}>
            {data?.bumps.map(bump => (
              <BumpItem key={bump.id} {...bump} onClick={() => openModal(bump.id)} />
            ))}
          </If>
        </List>
        <ContainerPagination>
          <Pagination
            onChangePage={page => setPage(page)}
            onChangePerPage={perPage => setPerPage(perPage)}
            page={page}
            perPage={perPage}
            total={data?.total || 0}
            optionsPerPage={PAGINATION_OPTION_PER_PAGE}
          />
        </ContainerPagination>

        <If condition={bumpId}>
          <Preview open={open} bumpId={bumpId ?? 0} onClose={closeDrawer} alertComponent={refAlertDialog} />
        </If>
      </If>
    </>
  );
};

export default memo(ListOrderBump);
